import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { Attachment, FileUploadService, isAttachmentImage } from './file-upload.service';
import { ApiResponse } from './data.service';
import { environment } from '../../environments/environment';

export interface ImageNodeAttachmentMetadata {
  attachmentId: string;
  src: string;
  fileSizeBytes?: number;
  naturalWidth?: number;
  naturalHeight?: number;
  mimeType?: string;
  imageSourceType: 'attachment';
  isImage?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private readonly baseUrl = environment.apiBaseUrl || '/api';

  constructor(private fileUpload: FileUploadService) {}

  uploadImage(file: File, noteId?: string): Observable<ImageNodeAttachmentMetadata> {
    const safeNoteId = String(noteId ?? '').trim();
    if (!safeNoteId) {
      return throwError(() => new Error('A noteId is required to upload an image with the existing attachment API.'));
    }

    return this.fileUpload.uploadAttachment(file, safeNoteId).pipe(
      switchMap((response: ApiResponse<Attachment>) => {
        if (response.status !== 'success' || !response.data) {
          return throwError(() => new Error(response.error?.message ?? 'Image upload failed.'));
        }

        const metadata = this.mapAttachmentToImageMetadata(response.data);
        if (!metadata) {
          return throwError(() => new Error('Uploaded attachment is not a valid image.'));
        }

        return of(metadata);
      }),
    );
  }

  getImageMetadata(attachmentId: string): Observable<ImageNodeAttachmentMetadata | null> {
    const safeAttachmentId = String(attachmentId ?? '').trim();
    if (!safeAttachmentId) {
      return of(null);
    }

    return this.fileUpload.getAttachmentInfo(safeAttachmentId).pipe(
      map((attachment) => {
        if (!attachment) {
          return null;
        }
        return this.mapAttachmentToImageMetadata({
          ...attachment,
          _id: String(attachment._id ?? safeAttachmentId),
        } as Attachment);
      }),
    );
  }

  private mapAttachmentToImageMetadata(attachment: Attachment): ImageNodeAttachmentMetadata | null {
    if (!attachment?._id || !isAttachmentImage(attachment)) {
      return null;
    }

    const naturalWidth = this.toPositiveNumber(attachment.naturalWidth ?? attachment.imageWidth);
    const naturalHeight = this.toPositiveNumber(attachment.naturalHeight ?? attachment.imageHeight);
    const fileSizeBytes = this.toPositiveNumber(attachment.fileSizeBytes ?? attachment.size);
    const src = this.resolveAttachmentSrc(attachment);

    return {
      attachmentId: attachment._id,
      src,
      fileSizeBytes,
      naturalWidth,
      naturalHeight,
      mimeType: attachment.mimeType,
      imageSourceType: 'attachment',
      isImage: attachment.isImage === true ? true : undefined,
    };
  }

  private resolveAttachmentSrc(attachment: Attachment): string {
    const candidate = String(attachment.remoteUrl ?? attachment.localUrl ?? '').trim();
    if (candidate) {
      return candidate;
    }
    return `${this.baseUrl}/attachments/${encodeURIComponent(attachment._id)}/file`;
  }

  private toPositiveNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }
}
